import axios from 'axios'

import { COP_COMMON_ERROR_MESSAGES } from '../constants/common-error-messages'
import {
  getAllowedProductFamiliesInOrderUrl,
  getHtmlDocumentationByFamilyUrl,
  getHtmlDocumentationResourceUrl,
  getHtmlDocumentationUrl,
  getPatchManagementCategoriesUrl,
  getPatchManagementDownloadUrl,
  getRmaRequestFileUrl,
} from '../constants/service-endpoints'
import { ToastService } from './toast.service'
import { buildCopHeaders, getSilentToken } from './tokenUtils'

export interface ResourceFile {
  fileId: string
  name: string
  path: string
  length: number
  lastModified: string
  isHidden: boolean
}

export interface ResourceSubCategory {
  name: string
  files: ResourceFile[]
}

export interface ResourceCategory {
  name: string
  subCategories: ResourceSubCategory[]
}

export interface ResourceCategoriesResponse {
  '@odata.context'?: string
  value: ResourceCategory[]
}

export async function fetchResources(): Promise<ResourceCategoriesResponse | null> {
  try {
    const token = getSilentToken()
    const url = getPatchManagementCategoriesUrl()
    const headers = buildCopHeaders(token)
    const response = await axios.get<ResourceCategoriesResponse>(url, { headers })
    return response.data
  } catch (error) {
    const fallbackMessage = COP_COMMON_ERROR_MESSAGES['api-error'].message.replace(
        '{{action}}',
        COP_COMMON_ERROR_MESSAGES['api-error'].action['agreements-loading'],
    )
    const message = error instanceof Error && error.message ? error.message : fallbackMessage
    ToastService.error(message)
    return null
  }
}

function reportResourceError(error: unknown): void {
  const fallbackMessage = COP_COMMON_ERROR_MESSAGES['api-error'].message.replace(
      '{{action}}',
      COP_COMMON_ERROR_MESSAGES['api-error'].action['agreements-loading'],
  )
  const message = error instanceof Error && error.message ? error.message : fallbackMessage
  ToastService.error(message)
}

export async function fetchAllowedProductFamiliesInOrder(): Promise<string[]> {
  try {
    const token = getSilentToken()
    const url = getAllowedProductFamiliesInOrderUrl()
    const headers = buildCopHeaders(token)
    const response = await axios.get<string[]>(url, { headers })
    return Array.isArray(response.data) ? response.data : []
  } catch (error) {
    reportResourceError(error)
    return []
  }
}

export async function fetchHtmlDocumentationByFamily(productFamily: string): Promise<string[]> {
  if (typeof productFamily !== 'string' || productFamily.trim() === '') {
    return []
  }
  try {
    const token = getSilentToken()
    const url = getHtmlDocumentationByFamilyUrl(productFamily)
    const headers = buildCopHeaders(token)
    const response = await axios.get<string[]>(url, { headers })
    return Array.isArray(response.data) ? response.data : []
  } catch (error) {
    reportResourceError(error)
    return []
  }
}

function triggerBlobDownload(blob: Blob, fileName: string): void {
  const objectUrl = window.URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.setAttribute('download', fileName)
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 0)
}

export async function fetchResourceFileBlob(fileId: string): Promise<Blob | null> {
  try {
    const token = getSilentToken()
    const url = getPatchManagementDownloadUrl(fileId)
    const headers = buildCopHeaders(token)
    const response = await axios.get<Blob>(url, { headers, responseType: 'blob' })
    return response.data
  } catch (error) {
    reportResourceError(error)
    return null
  }
}

export async function downloadResourceFile(fileId: string, fileName: string): Promise<boolean> {
  const blob = await fetchResourceFileBlob(fileId)
  if (!blob) {
    return false
  }
  triggerBlobDownload(blob, fileName)
  return true
}

export async function fetchRmaRequestFileBlob(fileName: string): Promise<Blob | null> {
  try {
    const token = getSilentToken()
    const url = getRmaRequestFileUrl(fileName)
    const headers = buildCopHeaders(token)
    const response = await axios.get<Blob>(url, { headers, responseType: 'blob' })
    return response.data
  } catch (error) {
    reportResourceError(error)
    return null
  }
}

export async function downloadRmaRequestFile(fileName: string): Promise<boolean> {
  const blob = await fetchRmaRequestFileBlob(fileName)
  if (!blob) {
    return false
  }
  triggerBlobDownload(blob, fileName)
  return true
}

export async function openRmaRequestFile(fileName: string): Promise<boolean> {
  const blob = await fetchRmaRequestFileBlob(fileName)
  if (!blob) {
    return false
  }
  const objectUrl = window.URL.createObjectURL(blob)
  window.open(objectUrl, '_blank', 'noopener,noreferrer')
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000)
  return true
}

export interface HtmlDocumentationResponse {
  relativeFilePath?: string
  sasToken?: string
  encryptedData?: string
  [key: string]: unknown
}

export async function fetchHtmlDocumentation(id: string): Promise<HtmlDocumentationResponse | null> {
  try {
    const token = getSilentToken()
    const url = getHtmlDocumentationUrl(id)
    const headers = buildCopHeaders(token)
    const response = await axios.get<HtmlDocumentationResponse>(url, { headers })
    return response.data
  } catch (error) {
    reportResourceError(error)
    return null
  }
}


export async function openHtmlDocumentation(id: string): Promise<boolean> {
  const newTab = window.open('', '_blank')
  if (newTab) {
    newTab.opener = null
  }

  const doc = await fetchHtmlDocumentation(id)
  const relativeFilePath = doc?.relativeFilePath?.trim()
  if (!doc || !relativeFilePath) {
    newTab?.close()
    return false
  }

  const baseUrl = getHtmlDocumentationResourceUrl(relativeFilePath)
  const params = new URLSearchParams()
  if (doc.sasToken) {
    params.set('sasToken', doc.sasToken)
  }
  if (doc.encryptedData) {
    params.set('encryptedData', decodeURIComponent(doc.encryptedData))
  }
  const query = params.toString()
  const url = query ? `${baseUrl}?${query}` : baseUrl
  if (newTab) {
    newTab.location.href = url
  } else {
    window.open(url, '_blank', 'noopener,noreferrer')
  }
  return true
}